import { BASE_AUTH_API, fetchAPI } from '@/services/api';
import {
    exportSpkiBase64,
    generateAesKeyRaw,
    generateClientRsaKeyPair,
    importAesKey,
    importServerPublicKey,
    wrapAesKeyRsaOaep,
} from '@/utils/apiCrypto';

const SS_SESSION_ID = 'cryptoSessionId';
const SS_AES_KEY = 'cryptoAesKeyB64';
const SS_CLIENT_PRIV = 'cryptoClientPrivJwk';
const SS_SERVER_PUB = 'cryptoServerPubPem';
const SS_KEY_ID = 'cryptoKeyId';

export type ActiveCryptoSession = {
    sessionId: string;
    aesKey: CryptoKey;
    clientPrivateKey: CryptoKey;
    serverPublicKeyPem: string;
    /** Cached RSASSA verify key — avoid re-importing SPKI on every response. */
    serverVerifyKey?: CryptoKey;
    keyId: string;
};

let memorySession: ActiveCryptoSession | null = null;

/** In-flight RSA-2048 pair (slow in browser). Started on login page so login click does not wait. */
let warmPairP: Promise<CryptoKeyPair> | null = null;
/** In-flight server SPKI fetch (plaintext whitelist). */
let warmServerKeyP: Promise<{ publicKey: string; keyId: string }> | null = null;
/** Single-flight negotiate — login redirect + menu fetch must share one handshake. */
let negotiateInFlight: Promise<ActiveCryptoSession> | null = null;

function b64Encode(bytes: Uint8Array): string {
    let s = '';
    for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
    return btoa(s);
}

function b64Decode(b64: string): Uint8Array {
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
}

/**
 * Payload cipher requires HTTPS (crypto.subtle). Controlled by
 * VITE_CRYPTO_PAYLOAD_ENABLED (UAT/prod build: true; local dev: false).
 */
export function isPayloadCipherEnabled(): boolean {
    const v = import.meta.env.VITE_CRYPTO_PAYLOAD_ENABLED;
    return v === 'true' || v === true;
}

export function clearCryptoSession(): void {
    memorySession = null;
    try {
        sessionStorage.removeItem(SS_SESSION_ID);
        sessionStorage.removeItem(SS_AES_KEY);
        sessionStorage.removeItem(SS_CLIENT_PRIV);
        sessionStorage.removeItem(SS_SERVER_PUB);
        sessionStorage.removeItem(SS_KEY_ID);
    } catch {
        // ignore
    }
}

export async function getActiveCryptoSession(): Promise<ActiveCryptoSession | null> {
    if (memorySession) return memorySession;
    try {
        const sessionId = sessionStorage.getItem(SS_SESSION_ID);
        const aesB64 = sessionStorage.getItem(SS_AES_KEY);
        const privJwk = sessionStorage.getItem(SS_CLIENT_PRIV);
        const serverPem = sessionStorage.getItem(SS_SERVER_PUB);
        const keyId = sessionStorage.getItem(SS_KEY_ID) || 'rsa-2048-v1';
        if (!sessionId || !aesB64 || !privJwk || !serverPem) return null;
        const aesKey = await importAesKey(b64Decode(aesB64));
        const clientPrivateKey = await crypto.subtle.importKey(
            'jwk',
            JSON.parse(privJwk) as JsonWebKey,
            { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' },
            false,
            ['sign'],
        );
        memorySession = {
            sessionId,
            aesKey,
            clientPrivateKey,
            serverPublicKeyPem: serverPem,
            keyId,
        };
        return memorySession;
    } catch {
        clearCryptoSession();
        return null;
    }
}

/**
 * Start RSA keygen + server public key fetch before the user submits login.
 * WHY: UAT cipher waits ~1–5s on generateKey(2048) if started only after /login returns.
 */
export function warmCryptoHandshake(): void {
    if (!isPayloadCipherEnabled()) return;
    if (typeof crypto === 'undefined' || !crypto.subtle) return;
    if (!warmPairP) {
        warmPairP = generateClientRsaKeyPair().catch((err) => {
            warmPairP = null;
            throw err;
        });
    }
    if (!warmServerKeyP) {
        warmServerKeyP = fetchAPI<{ publicKey: string; keyId: string }>(
            `${BASE_AUTH_API}/crypto/server-public-key`,
            { method: 'GET' },
        ).catch((err) => {
            warmServerKeyP = null;
            throw err;
        });
    }
}

/**
 * After login: reuse warmed key material, wrap AES, POST session.
 * Uses plaintext paths (whitelisted). Call before any ciphered business API.
 * Concurrent callers share one in-flight negotiate (login redirect vs menu prefetch).
 */
export async function negotiateCryptoSession(): Promise<ActiveCryptoSession> {
    if (negotiateInFlight) return negotiateInFlight;

    negotiateInFlight = (async () => {
        warmCryptoHandshake();
        clearCryptoSession();

        const [serverKey, clientPair, aesRaw] = await Promise.all([
            warmServerKeyP as Promise<{ publicKey: string; keyId: string }>,
            warmPairP as Promise<CryptoKeyPair>,
            generateAesKeyRaw(),
        ]);
        const [serverPublicKey, clientPublicKey] = await Promise.all([
            importServerPublicKey(serverKey.publicKey),
            exportSpkiBase64(clientPair.publicKey),
        ]);
        const wrappedSessionKey = await wrapAesKeyRsaOaep(serverPublicKey, aesRaw);

        const session = await fetchAPI<{ sessionId: string }>(`${BASE_AUTH_API}/crypto/session`, {
            method: 'POST',
            body: JSON.stringify({ wrappedSessionKey, clientPublicKey }),
        });
        warmPairP = null;

        const aesKey = await importAesKey(aesRaw);
        const privJwk = await crypto.subtle.exportKey('jwk', clientPair.privateKey);
        try {
            sessionStorage.setItem(SS_SESSION_ID, session.sessionId);
            sessionStorage.setItem(SS_AES_KEY, b64Encode(aesRaw));
            sessionStorage.setItem(SS_CLIENT_PRIV, JSON.stringify(privJwk));
            sessionStorage.setItem(SS_SERVER_PUB, serverKey.publicKey);
            sessionStorage.setItem(SS_KEY_ID, serverKey.keyId || 'rsa-2048-v1');
        } catch {
            // sessionStorage may be unavailable; keep memory only
        }

        memorySession = {
            sessionId: session.sessionId,
            aesKey,
            clientPrivateKey: clientPair.privateKey,
            serverPublicKeyPem: serverKey.publicKey,
            keyId: serverKey.keyId || 'rsa-2048-v1',
        };
        return memorySession;
    })().finally(() => {
        negotiateInFlight = null;
    });

    return negotiateInFlight;
}
